import { AxiosError } from 'axios';
import { apiClient } from './client';
import { useAuthStore } from '../store/authStore';

/**
 * 화면잠금 해제 — 본인 비밀번호 확인 (screen-lock, 2026-08-19).
 *
 * 기본 경로는 확인 전용 라우트 `POST /auth/verify-password` 다(아무것도 바꾸지 않는다).
 *
 * **구 서버 대비 폴백** — 그 라우트가 아직 없는 서버(404)에서는 로그인 라우트로 같은 자격증명을
 * 확인한다. 이렇게 해두면 "프론트가 먼저 올라가면 아무도 잠금을 못 푼다" 는 배포 순서 결합이
 * 사라진다. 로그인은 같은 사용자로 쿠키를 새로 받으므로 세션은 그대로 이어진다.
 * (MFA 가 걸린 관리자 계정은 재로그인이 mfa-pending 으로 갈라지므로 폴백에서는 실패로 본다 —
 *  그 경우 잠금 화면의 "로그아웃하고 다시 로그인" 으로 안내된다.)
 */
export type UnlockResult = 'ok' | 'invalid' | 'sso' | 'mfa' | 'error';

export async function verifyUnlockPassword(password: string): Promise<UnlockResult> {
  try {
    await apiClient.post('/auth/verify-password', { password });
    return 'ok';
  } catch (err) {
    const status = err instanceof AxiosError ? err.response?.status : undefined;
    const code = err instanceof AxiosError
      ? (err.response?.data as { code?: string } | undefined)?.code
      : undefined;
    if (status === 401 || code === 'INVALID_PASSWORD') return 'invalid';
    if (status === 409 || code === 'SSO_ACCOUNT') return 'sso';
    if (status === 404) return verifyByLogin(password);
    return 'error';
  }
}

async function verifyByLogin(password: string): Promise<UnlockResult> {
  const email = useAuthStore.getState().user?.email;
  if (!email) return 'error';
  try {
    const { data } = await apiClient.post<Record<string, unknown>>('/auth/login', { email, password });
    if (data && typeof data === 'object' && 'mfa_required' in data && data.mfa_required) return 'mfa';
    return 'ok';
  } catch (err) {
    const status = err instanceof AxiosError ? err.response?.status : undefined;
    if (status === 401) return 'invalid';
    return 'error';
  }
}
